import { t } from "@lingui/macro";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useToast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";
import { useConfirmEmailChange } from "@/client/services/auth";
import { useAuthStore } from "@/client/stores/auth";

export const ConfirmEmailChangePage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { confirmEmailChange, loading } = useConfirmEmailChange();
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    const run = async () => {
      await confirmEmailChange({ token });
      await queryClient.invalidateQueries({ queryKey: ["user"] });

      toast({
        variant: "success",
        title: t`Your email address has been updated.`,
        description: t`Check the new inbox for a verification link.`,
      });

      navigate(isLoggedIn ? "/dashboard/settings" : "/auth/login", { replace: true });
    };

    void run();
  }, [confirmEmailChange, isLoggedIn, navigate, toast, token]);

  return (
    <div className="space-y-6">
      <Helmet>
        <title>
          {t`Confirm your email change`} - {t`CareerBldr`}
        </title>
      </Helmet>

      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">{t`Confirm your email change`}</h2>
        <p className="leading-relaxed opacity-75">
          {token
            ? t`We're confirming this change now. If the link is expired, request a new one from Settings.`
            : t`This link is missing a token. Request a new one from Settings while logged in.`}
        </p>
      </div>

      <Button asChild disabled={loading}>
        <Link to={isLoggedIn ? "/dashboard/settings" : "/auth/login"}>
          {isLoggedIn ? t`Back to Settings` : t`Go to Login`}
          <ArrowRight className="ml-2" />
        </Link>
      </Button>
    </div>
  );
};
